import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Home from './pages/Home';
import Login from './pages/Login';
import FindUsername from './pages/FindUsername';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SignupStart from './pages/signup/SignupStart';
import SignupVerify from './pages/signup/SignupVerify';
import SignupTerms from './pages/signup/SignupTerms';
import SignupForm from './pages/signup/SignupForm';
import SignupComplete from './pages/signup/SignupComplete';
import Greeting from './pages/Greeting';
import AboutGTC from './pages/AboutGTC';
import Facilities from './pages/Facilities';
import Location from './pages/Location';
import ProgramList from './pages/ProgramList';
import ProgramCategory from './pages/ProgramCategory';
import ProgramDetail from './pages/ProgramDetail';
import AllPrograms from './pages/AllPrograms';
import CurriculumInfo from './pages/CurriculumInfo';
import NoticeList from './pages/NoticeList';
import NoticeDetail from './pages/NoticeDetail';
import Gallery from './pages/Gallery';
import GalleryDetail from './pages/GalleryDetail';
import MyPage from './pages/MyPage';
import Payment from './pages/Payment';
import DistanceEducationIntro from './pages/DistanceEducationIntro';
import DistanceEducationRules from './pages/DistanceEducationRules';
import LevelTest from './pages/LevelTest';
import LearningHub from './pages/LearningHub';
import LMS from './pages/LMS';
import GroupApplication from './pages/GroupApplication';
import FAQ from './pages/FAQ';
import FAQDetail from './pages/FAQDetail';
import Reviews from './pages/Reviews';
import Survey from './pages/Survey';
import Materials from './pages/Materials';
import MaterialDetail from './pages/MaterialDetail';
import Search from './pages/Search';
import ComponentPreview from './pages/ComponentPreview';
import NotFound from './pages/NotFound';
import AdminLayout from './components/AdminLayout';
import AdminLogin from './pages/admin/AdminLogin';
import Dashboard from './pages/admin/Dashboard';
import CourseList from './pages/admin/CourseList';
import CourseDetail from './pages/admin/CourseDetail';
import CourseForm from './pages/admin/CourseForm';
import ClassManagement from './pages/admin/ClassManagement';
import StudentManagement from './pages/admin/StudentManagement';
import MemberManagement from './pages/admin/MemberManagement';
import AdminNoticeList from './pages/admin/NoticeList';
import NoticeForm from './pages/admin/NoticeForm';
import AdminGalleryList from './pages/admin/GalleryList';
import GalleryForm from './pages/admin/GalleryForm';
import PageContentManagement from './pages/admin/PageContentManagement';
import RefundManagement from './pages/admin/RefundManagement';
import AdminFAQList from './pages/admin/FAQList';
import FAQForm from './pages/admin/FAQForm';
import Statistics from './pages/admin/Statistics';
import AdminMaterialList from './pages/admin/MaterialList';
import MaterialForm from './pages/admin/MaterialForm';
import PopupManagement from './pages/admin/PopupManagement';
import { AuthProvider } from './contexts/AuthContext';
import { CourseProvider } from './contexts/CourseContext';
import { ApplicationProvider } from './contexts/ApplicationContext';
import { NoticeProvider } from './contexts/NoticeContext';
import { PageContentProvider } from './contexts/PageContentContext';
import { GalleryProvider } from './contexts/GalleryContext';
import { FAQProvider } from './contexts/FAQContext';
import { MaterialProvider } from './contexts/MaterialContext';
import ScrollToTop from './components/ScrollToTop';

function App() {
  // 개발 환경에서만 마이그레이션 함수 로드 (일회성)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      import('./utils/runMigration').then(module => {
        (window as any).runMigration = module.default;
        console.log('✅ 마이그레이션 함수가 로드되었습니다. 콘솔에서 window.runMigration()을 실행하세요.');
      });
    }
  }, []);

  return (
    <HelmetProvider>
    <AuthProvider>
      <CourseProvider>
        <ApplicationProvider>
          <NoticeProvider>
            <PageContentProvider>
              <GalleryProvider>
              <FAQProvider>
              <MaterialProvider>
                <Router>
                  <ScrollToTop />
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/find-username" element={<FindUsername />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/" element={<Home />} />

                    {/* 통합검색 */}
                    <Route path="/search" element={<Search />} />

                    {/* 컴포넌트 미리보기 (개발용 - 프로덕션 빌드에서 제외) */}
                    {process.env.NODE_ENV === 'development' && (
                      <Route path="/preview" element={<ComponentPreview />} />
                    )}

                    {/* 회원가입 */}
                    <Route path="/signup" element={<SignupStart />} />
                    <Route path="/signup/verify" element={<SignupVerify />} />
                    <Route path="/signup/terms" element={<SignupTerms />} />
                    <Route path="/signup/form" element={<SignupForm />} />
                    <Route path="/signup/complete" element={<SignupComplete />} />

                    {/* 센터소개 */}
                    <Route path="/about/greeting" element={<Greeting />} />
                    <Route path="/about/gtc" element={<AboutGTC />} />
                    <Route path="/about/facilities" element={<Facilities />} />
                    <Route path="/about/location" element={<Location />} />

                    {/* 교육과정 (Programs/Courses) */}
                    <Route path="/curriculum" element={<CurriculumInfo />} />
                    <Route path="/programs" element={<ProgramList />} />
                    <Route path="/programs/category/:category" element={<ProgramCategory />} />
                    <Route path="/programs/:id" element={<ProgramDetail />} />
                    <Route path="/courses" element={<ProgramList />} />
                    <Route path="/courses/:id" element={<ProgramDetail />} />

                    {/* 공지사항 */}
                    <Route path="/notice" element={<NoticeList />} />
                    <Route path="/notice/:id" element={<NoticeDetail />} />
                    <Route path="/notice/materials" element={<Materials />} />
                    <Route path="/notice/materials/:id" element={<MaterialDetail />} />

                    {/* 갤러리 */}
                    <Route path="/gallery" element={<Gallery />} />
                    <Route path="/gallery/:id" element={<GalleryDetail />} />

                    {/* 수강신청 */}
                    <Route path="/application" element={<AllPrograms />} />
                    <Route path="/application/register" element={<AllPrograms />} />
                    {/* LMS */}
                    <Route path="/lms" element={<LMS />} />
                    <Route path="/lms/level-test" element={<LevelTest />} />
                    <Route path="/lms/learning-hub" element={<LearningHub />} />
                    <Route path="/application/group" element={<GroupApplication />} />

                    {/* 참여마당 */}
                    <Route path="/community" element={<FAQ />} />
                    <Route path="/community/faq" element={<FAQ />} />
                    <Route path="/community/faq/:id" element={<FAQDetail />} />
                    <Route path="/community/reviews" element={<Reviews />} />
                    <Route path="/community/survey" element={<Survey />} />

                    {/* 마이페이지 */}
                    <Route path="/mypage" element={<MyPage />} />

                    {/* 결제 (Phase 3) */}
                    <Route path="/payment/:applicationId" element={<Payment />} />

                    {/* 원격평생교육원 */}
                    <Route path="/distance-education/intro" element={<DistanceEducationIntro />} />
                    <Route path="/distance-education/rules" element={<DistanceEducationRules />} />

                    {/* Admin Login (별도 페이지) */}
                    <Route path="/admin/login" element={<AdminLogin />} />

                    {/* Admin Routes */}
                    <Route path="/admin" element={<AdminLayout />}>
                      <Route index element={<Dashboard />} />
                      <Route path="courses" element={<CourseList />} />
                      <Route path="courses/new" element={<CourseForm />} />
                      <Route path="courses/edit/:id" element={<CourseForm />} />
                      <Route path="courses/:id" element={<CourseDetail />} />
                      <Route path="class-management" element={<ClassManagement />} />
                      <Route path="students" element={<StudentManagement />} />
                      <Route path="members" element={<MemberManagement />} />
                      <Route path="notices" element={<AdminNoticeList />} />
                      <Route path="notices/new" element={<NoticeForm />} />
                      <Route path="notices/edit/:id" element={<NoticeForm />} />
                      <Route path="gallery" element={<AdminGalleryList />} />
                      <Route path="gallery/new" element={<GalleryForm />} />
                      <Route path="gallery/edit/:id" element={<GalleryForm />} />
                      <Route path="page-contents" element={<PageContentManagement />} />
                      <Route path="refunds" element={<RefundManagement />} />
                      <Route path="faqs" element={<AdminFAQList />} />
                      <Route path="faqs/new" element={<FAQForm />} />
                      <Route path="faqs/edit/:id" element={<FAQForm />} />
                      <Route path="materials" element={<AdminMaterialList />} />
                      <Route path="materials/new" element={<MaterialForm />} />
                      <Route path="materials/edit/:id" element={<MaterialForm />} />
                      <Route path="statistics" element={<Statistics />} />
                      <Route path="popups" element={<PopupManagement />} />
                    </Route>

                    {/* 404 Not Found */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Router>
              </MaterialProvider>
              </FAQProvider>
              </GalleryProvider>
            </PageContentProvider>
          </NoticeProvider>
        </ApplicationProvider>
      </CourseProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
